import { FeedbackSection, ScoreData, ScoreRowCard } from '@/components/Score';
import { SimulationScoreFeedbackItemProps } from '../types';
import { useMemo } from 'react';

/**
 * Component to display feedback for a specific score category in the simulation score feedback.
 * It renders a ScoreRowCard with the category title, feedback, and score information.
 */
export const SimulationScoreFeedbackItem: React.FC<SimulationScoreFeedbackItemProps> = ({
  category,
  categoryPassed,
  categorySkillLevel,
  categoryType,
  isPrinting,
  scoreCardId,
  sessionId,
  sharedWithManager,
  simulationType,
  userId,
}) => {
  // ==================================================================
  // Memoized values
  // ==================================================================

  /**
   * Memoized value for the category title, derived from the category prop.
   */
  const categoryTitle: string = useMemo(() => {
    return category?.title || '';
  }, [category]);

  /**
   * Memoized value for the feedback sections, derived from the category content.
   * Each content item is transformed into a FeedbackSection with a title and list of items.
   */
  const feedback: FeedbackSection[] = useMemo(() => {
    return (
      category?.content?.map((content) => ({
        title: content?.title,
        items: content?.items ? content.items.map((item) => ({ text: item })) : [],
      })) || []
    );
  }, [category]);

  /**
   * Memoized value for the score data, derived from the category's score information.
   * It includes the max value, pass status, type, and actual score value.
   */
  const scoreData: ScoreData = useMemo(() => {
    let percentageValue = 0;
    if ('scorePercentage' in category) {
      percentageValue = category.scorePercentage as number;
    } else {
      percentageValue = category?.scoreMax ? ((category?.score || 0) / category.scoreMax) : 0;
    }

    return {
      maxValue: category?.scoreMax,
      percentageValue,
      passed: categoryPassed,
      type: categoryType,
      value: category?.score,
    };
  }, [category, categoryType, categoryPassed]);

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <ScoreRowCard
      category={categoryTitle}
      className="print-avoid-break"
      feedback={feedback}
      forceExpanded={isPrinting}
      pass={categoryPassed}
      score={scoreData}
      scoreCardId={scoreCardId}
      sessionId={sessionId}
      sharedWithManager={sharedWithManager}
      simulationType={simulationType}
      skillLevel={categorySkillLevel}
      type={categoryType}
      userId={userId}
    />
  );
};

SimulationScoreFeedbackItem.displayName = 'SimulationScoreFeedbackItem';

export default SimulationScoreFeedbackItem;
